import React ,{ useEffect, useState} from "react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import ToastDefault from "../../components/Toast";
import { RegistrationAction } from "../../redux/actions/UserActions";
import { useNavigate, Link } from "react-router-dom";
import { stringify } from "querystring";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from 'react-helmet';
import * as Yup from "yup";
import { toast } from 'react-toastify';
import './registration.css';

type SeoContent = {
  meta_title: string;
  meta_description?: string;
  // Add more fields as needed
};

type RegistrationErrors = {
  name?: string;
  email?: string;
  password?: string;
  confirm_password?: string;
};

const Registration: React.FC = () => {


    const dispatch = useDispatch<any>();
    const navigate = useNavigate();
    const { status, data, error, loading } = useSelector((state: any) => state.registrationState);

    const [soeContent, setSoeContent] = useState<SeoContent | null>(null);
    const [errors, setErrors] = useState<RegistrationErrors>({});
    const [loadingM, setLoadingM] = useState(false);

    const validationSchema = Yup.object().shape({
      name: Yup.string().required("Full name is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .matches(/\d/, "Password must contain at least one number")
        .matches(/[!@#$%^&*(),.?":{}|<>]/, "Password must contain at least one special character")
        .required("Password is required"),
      confirm_password: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Confirm password is required"),
    });

    const handleSubmit = async (e: React.FormEvent) => {
      setLoadingM(true);
      e.preventDefault();
      setErrors({});
      const formData = {
        name: (document.getElementById("name") as HTMLInputElement).value,
        email: (document.getElementById("email") as HTMLInputElement).value,
        password: (document.getElementById("password") as HTMLInputElement).value,
        confirm_password: (document.getElementById("confirm_password") as HTMLInputElement).value,
        user_type:"Manual"
      };

      try {
        await validationSchema.validate(formData, { abortEarly: false });
        console.log("Form submitted:", formData);
        await dispatch(RegistrationAction(formData));
        // dispatch signup action here
      } catch (err: any) {
        if (err.inner) {
          const formErrors: RegistrationErrors = {};
          err.inner.forEach((validationErr: any) => {
            const key = validationErr.path as keyof RegistrationErrors;
            formErrors[key] = validationErr.message;
          });
          setErrors(formErrors);
          setTimeout(() => {
            setLoadingM(false);
          }, 200);
        }
      }
    };

    useEffect(() => {
      console.log({ status, data, error, loading })
        if(status){
            dispatch(RegistrationAction('RESET'));
            toast.success(data?.message);
            setTimeout(() => {
              setLoadingM(false);
              navigate("/login");
            }, 3000);
        }
        if(error) {
          dispatch(RegistrationAction('RESET'));
          setLoadingM(false);
        }

    }, [dispatch, status , error])

  return (
    <>
    <Helmet>
      <title>{`${soeContent?.meta_title || 'Registration'}`}</title>
      <meta name="title" content={soeContent?.meta_title || 'E-learning Platform'} />
      <meta name="description" content={soeContent?.meta_description || 'Learn with curated e-learning resources'} />
      <meta name="keywords" content="e-learning, tech-cards, cheat-sheets, tutorials" />
    </Helmet>

      <Header />

      <div className="signup-wrapper">
        <div className="signup-card">
          {/* Left Side Image/Info Section */}
          <div className="image-section">
            <div className="image-content">
              <h2>Launch Your Tech Career with Uplatz</h2>
              <p>Join our community of learners and get access to industry-leading courses, expert mentorship, and career support.</p>
              <ul className="features-list">
                <li><i className="bi bi-check-lg"></i> 1000+ industry-relevant courses</li>
                <li><i className="bi bi-check-lg"></i> Hands-on projects & certifications</li>
                <li><i className="bi bi-check-lg"></i> 1:1 mentorship sessions</li>
                <li><i className="bi bi-check-lg"></i> Career guidance & placement support</li>
                <li><i className="bi bi-check-lg"></i> Flexible learning options</li>
              </ul>
              <div className="mt-4 text-center">
                <i className="bi bi-mortarboard fs-1"></i>
              </div>
            </div>
          </div>

          {/* Right Side Form Section */}
          <div className="form-section">
            <div className="text-center mb-4">
              <i className="bi bi-person-plus text-primary fs-1"></i>
              <h3 className="fw-bold mt-3">Create Your Account</h3>
              <p className="text-muted">Start your learning journey in just 30 seconds</p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Full Name */}
              <div className="mb-3">
                <label className="form-label"><i className="bi bi-person-fill"></i> Full Name</label>
                <input type="text" id="name" name="name" className={`form-control ${errors.name ? "is-invalid" : ""}`} placeholder="Enter your full name" />
                {errors.name && <div className="text-danger small">{errors.name}</div>}
              </div>

              {/* Email */}
              <div className="mb-3">
                <label className="form-label"><i className="bi bi-envelope-fill"></i> Email Address</label>
                <input type="email" id="email" name="email" className={`form-control ${errors.email ? "is-invalid" : ""}`} placeholder="Enter your email" />
                {errors.email && <div className="text-danger small">{errors.email}</div>}
              </div>

              {/* Password */}
              <div className="mb-3">
                <label className="form-label"><i className="bi bi-lock-fill"></i> Password</label>
                <input type="password" id="password" name="password" className={`form-control ${errors.password ? "is-invalid" : ""}`} placeholder="Create a password" />
                {errors.password && <div className="text-danger small">{errors.password}</div>}
                <div className="password-requirements mt-1">
                  <small className="text-muted">Must have at least 8 characters, 1 number, and 1 special character.</small>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="mb-4">
                <label className="form-label"><i className="bi bi-lock-fill"></i> Confirm Password</label>
                <input type="password" id="confirm_password" name="confirm_password" className={`form-control ${errors.confirm_password ? "is-invalid" : ""}`} placeholder="Confirm your password" />
                {errors.confirm_password && <div className="text-danger small">{errors.confirm_password}</div>}
              </div>

              {/* Submit Button */}
              <div className="d-grid mb-3">
                <button type="submit" className="btn btn-primary btn-lg fw-semibold" disabled={loadingM}>
                   {loadingM ? (<><span className="spinner-border spinner-border-sm me-2" role="status"  aria-hidden="true"></span>Logging in...</>) : (<><i className="bi bi-person-plus-fill me-2"></i>Create Account</>)}
                </button>
              </div>
              {/* Divider */}
              {/* <div className="divider">
                <span className="divider-text">OR CONTINUE WITH</span>
              </div>

              <div className="social-login">
                <a href="#" className="social-btn google-btn" title="Sign up with Google">
                  <i className="bi bi-google"></i>
                </a>
                <a href="#" className="social-btn facebook-btn" title="Sign up with Facebook">
                  <i className="bi bi-facebook"></i>
                </a>
                <a href="#" className="social-btn linkedin-btn" title="Sign up with LinkedIn">
                  <i className="bi bi-linkedin"></i>
                </a>
              </div> */}

              {/* Already Have Account */}
              <div className="text-center mt-4">
                <p className="mb-0">Already have an account?{" "}
                  <Link to={"/login"} className="text-decoration-none text-primary fw-semibold">Login</Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
      <ToastDefault/>
      <Footer />
    </>
  );
};

export default Registration;
